import {Filter, type FilterProvider, FilterProviderType} from '@github-ui/filter'
import {OrgFilterProvider, StaticFilterProvider} from '@github-ui/filter/providers'
import {
  BriefcaseIcon,
  CheckboxIcon,
  GlobeIcon,
  MultiSelectIcon,
  NoteIcon,
  OrganizationIcon,
  SingleSelectIcon,
  TypographyIcon,
} from '@primer/octicons-react'
import {useMemo} from 'react'

export function DefinitionsFilter({
  filterValue,
  onChange,
  onSubmit,
  businessSlug,
  className,
  showValueType,
}: {
  filterValue: string
  onChange: (value: string) => void
  onSubmit: (query: string) => void
  businessSlug?: string
  className?: string
  // Drop showValueType along with FF custom_properties_search_value_type
  showValueType?: boolean
}) {
  const providers = useMemo(() => {
    const providersList: FilterProvider[] = [new ManagedByFilterProvider()]
    if (showValueType) {
      providersList.push(new PropertyTypeFilterProvider())
    }

    if (businessSlug) {
      providersList.push(
        new OrgFilterProvider({
          filterTypes: {valueless: false, exclusive: true},
          businessSlug,
        }),
      )
    }
    return providersList
  }, [businessSlug, showValueType])

  return (
    <Filter
      id="properties-filter"
      label="Filter properties"
      className={className}
      providers={providers}
      filterValue={filterValue}
      onChange={onChange}
      onSubmit={request => onSubmit(request.raw)}
    />
  )
}

class ManagedByFilterProvider extends StaticFilterProvider {
  constructor() {
    const filter = {
      displayName: 'Managed by',
      key: 'managed-by',
      description: 'Include properties managed by the enterprise and/or any of its organizations',
      priority: 1,
      icon: BriefcaseIcon,
    }

    const values = [
      {value: 'enterprise', displayName: 'Enterprise', icon: GlobeIcon, priority: 1},
      {value: 'organization', displayName: 'Organization', icon: OrganizationIcon, priority: 2},
    ]

    super(filter, values, {filterTypes: {multiKey: false, multiValue: true, valueless: false, exclusive: true}})
    this.type = FilterProviderType.Select
  }
}

class PropertyTypeFilterProvider extends StaticFilterProvider {
  constructor() {
    const filter = {
      displayName: 'Property type',
      key: 'value-type',
      description: 'Filter definitions by type',
      priority: 1,
      icon: NoteIcon,
    }

    const values = [
      {value: 'text', displayName: 'Text', icon: TypographyIcon, priority: 1},
      {value: 'single_select', displayName: 'Single select', icon: SingleSelectIcon, priority: 2},
      {value: 'multi_select', displayName: 'Multi select', icon: MultiSelectIcon, priority: 3},
      {value: 'true_false', displayName: 'True/false', icon: CheckboxIcon, priority: 4},
    ]

    super(filter, values, {filterTypes: {multiKey: false, multiValue: true, valueless: false, exclusive: true}})
    this.type = FilterProviderType.Select
  }
}

try{ DefinitionsFilter.displayName ||= 'DefinitionsFilter' } catch {}